import { useAppSelector } from "@/redux/hooks";
import { useTheme } from "@mui/material";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getHeadersWithUniqueVisitorId } from "@brandclub/common-ui";
import { IconCompose, IconHome, IconSidebar } from "./ConciergeIcons";
import { HeaderWidget, HistoryList, Conversation } from "@brandclub/bc-widgets";
import { Drawer } from "@mui/material";

interface ConciergeSidebarProps {
  onConversationSelect?: (conversationId: string) => void;
  onComposeClick?: () => void;
  currentConversationId?: string | null;
}

const fetchConversations = async (): Promise<{
  conversations: Conversation[];
}> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch("/api-rewards/concierge/conversations", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch conversations");
  }

  return response.json();
};

interface SidebarButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  isExpanded: boolean;
  ariaLabel: string;
  textColor?: string;
}

const SidebarButton = ({
  icon,
  label,
  onClick,
  isExpanded,
  ariaLabel,
  textColor,
}: SidebarButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="tw:relative">
      <button
        onClick={onClick}
        aria-label={ariaLabel}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:rounded tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:w-full"
      >
        {/* Icon container with fixed width for alignment, but icon renders at its natural size */}
        <div
          className="tw:relative tw:shrink-0"
          style={{ width: 20, height: 20 }}
        >
          <div className="tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2">
            {icon}
          </div>
        </div>
        <span
          className="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150"
          style={{
            opacity: isExpanded ? 1 : 0,
            width: isExpanded ? "auto" : 0,
            color: textColor,
          }}
        >
          {label}
        </span>
      </button>
      {/* Tooltip - only show when collapsed and hovered */}
      {!isExpanded && isHovered && (
        <div
          className="tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:bg-primary tw:text-white tw:rounded-lg tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap"
          style={{
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "18px",
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};

// Mobile header button - simpler styling for horizontal layout
interface MobileHeaderButtonProps {
  icon: React.ReactNode;
  onClick?: () => void;
  ariaLabel: string;
}

const MobileHeaderButton = ({
  icon,
  onClick,
  ariaLabel,
}: MobileHeaderButtonProps) => (
  <button
    onClick={onClick}
    aria-label={ariaLabel}
    className="tw:flex tw:items-center tw:justify-center tw:w-10 tw:h-10 tw:rounded tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5"
  >
    {icon}
  </button>
);

export const ConciergeSidebar = ({
  onConversationSelect,
  onComposeClick,
  currentConversationId,
}: ConciergeSidebarProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const theme = useTheme();

  const { data, isLoading, error } = useQuery<{
    conversations: Conversation[];
  }>({
    queryKey: ["conversations"],
    queryFn: fetchConversations,
    enabled: isExpanded || isMobileDrawerOpen,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const getInitials = () => {
    const firstName = userProfile?.given_name || "";
    const lastName = userProfile?.family_name || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    return "";
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMobileToggle = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const handleHomeClick = () => {
    window.location.href = window.location.origin;
  };

  const handleConversationClick = (conversationId: string) => {
    onConversationSelect?.(conversationId);
  };

  const handleMobileConversationSelect = (conversationId: string) => {
    handleConversationClick(conversationId);
    setIsMobileDrawerOpen(false);
  };

  const initials = getInitials();
  const isSignedIn = !!userProfile?.userId;

  const [avatarHovered, setAvatarHovered] = useState(false);

  return (
    <>
      {/* Mobile Header - shows only sidebar icon on mobile */}
      <header
        className="tw:flex tw:md:hidden tw:items-center tw:px-4 tw:py-2 tw:border-b tw:border-[#dcdddc] tw:shrink-0"
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <HeaderWidget
          widgetId="concierge-sidebar-header"
          displayName="Sidebar Header"
        >
          {(state) => (
            <MobileHeaderButton
              icon={
                <IconSidebar
                  size={state.sidebarIconSize ?? state.iconSize}
                  style={{ color: state.sidebarIconColor || state.iconColor }}
                />
              }
              onClick={handleMobileToggle}
              ariaLabel="View conversation history"
            />
          )}
        </HeaderWidget>
      </header>

      {/* Mobile Drawer - shows conversation history on mobile */}
      <Drawer
        anchor="left"
        open={isMobileDrawerOpen}
        onClose={() => setIsMobileDrawerOpen(false)}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
        }}
      >
        <div
          className="tw:flex tw:h-full tw:flex-col"
          style={{ backgroundColor: theme.palette.background.default }}
        >
          {/* Header with Home and New Chat buttons */}
          <div className="tw:flex tw:items-center tw:justify-between tw:px-3 tw:py-3 tw:border-b tw:border-[#dcdddc]">
            <HeaderWidget
              widgetId="concierge-sidebar-header"
              displayName="Sidebar Header"
            >
              {(state) => (
                <>
                  <span className="tw:text-sm tw:font-semibold tw:text-primary">
                    Chats
                  </span>
                  <div className="tw:flex tw:items-center tw:gap-1">
                    <MobileHeaderButton
                      icon={
                        <IconHome
                          size={state.homeIconSize ?? state.iconSize}
                          style={{
                            color: state.homeIconColor || state.iconColor,
                          }}
                        />
                      }
                      onClick={() => {
                        handleHomeClick();
                        setIsMobileDrawerOpen(false);
                      }}
                      ariaLabel="Go to home"
                    />
                    <MobileHeaderButton
                      icon={
                        <IconCompose
                          size={state.composeIconSize ?? state.iconSize}
                          style={{
                            color: state.composeIconColor || state.iconColor,
                          }}
                        />
                      }
                      onClick={() => {
                        onComposeClick?.();
                        setIsMobileDrawerOpen(false);
                      }}
                      ariaLabel="Start new chat"
                    />
                  </div>
                </>
              )}
            </HeaderWidget>
          </div>

          {/* History List - same as desktop */}
          <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden">
            <div className="tw:flex-1 tw:overflow-y-auto tw:mt-2">
              <HistoryList
                conversations={data?.conversations || []}
                isLoading={isLoading}
                error={error ? "Failed to load conversations" : null}
                onSelect={handleMobileConversationSelect}
                currentConversationId={currentConversationId}
                widgetId="concierge-sidebar-history"
                displayName="Sidebar History"
                showHeader={false}
                emptyMessage="No conversations yet"
                initialState={{
                  // Customize styling for sidebar - more compact
                  itemPaddingX: 8,
                  itemPaddingY: 4,
                  itemTitleLineClamp: 1,
                  timestampMarginTop: 2,
                  timestampFontSize: 11,
                  drawerBgColor: "transparent",
                }}
              />
            </div>
          </div>
        </div>
      </Drawer>

      {/* Desktop Sidebar - shows only on desktop */}
      <aside
        className="tw:hidden tw:md:flex tw:flex-col tw:shrink-0 tw:h-full tw:border-r tw:border-[#dcdddc]"
        style={{
          backgroundColor: theme.palette.background.default,
          width: isExpanded ? 248 : 40,
          transition: "width 0.25s ease-in-out",
        }}
      >
        {/* Top icons */}
        <div className="tw:flex tw:flex-col tw:pt-3">
          <HeaderWidget
            widgetId="concierge-sidebar-header"
            displayName="Sidebar Header"
          >
            {(state) => (
              <>
                {/* Home - navigate to root */}
                <SidebarButton
                  icon={
                    <IconHome
                      size={state.homeIconSize ?? state.iconSize}
                      style={{ color: state.homeIconColor || state.iconColor }}
                    />
                  }
                  label="Home"
                  onClick={handleHomeClick}
                  isExpanded={isExpanded}
                  ariaLabel="Go to home"
                  textColor={state.titleColor}
                />

                {/* Toggle sidebar */}
                <SidebarButton
                  icon={
                    <IconSidebar
                      size={state.sidebarIconSize ?? state.iconSize}
                      style={{
                        color: state.sidebarIconColor || state.iconColor,
                      }}
                    />
                  }
                  label={isExpanded ? "Close sidebar" : "Open sidebar"}
                  onClick={handleToggle}
                  isExpanded={isExpanded}
                  ariaLabel={isExpanded ? "Close sidebar" : "Open sidebar"}
                  textColor={state.titleColor}
                />

                {/* Compose new conversation */}
                <SidebarButton
                  icon={
                    <IconCompose
                      size={state.composeIconSize ?? state.iconSize}
                      style={{
                        color: state.composeIconColor || state.iconColor,
                      }}
                    />
                  }
                  label="New chat"
                  onClick={onComposeClick}
                  isExpanded={isExpanded}
                  ariaLabel="Start new chat"
                  textColor={state.titleColor}
                />
              </>
            )}
          </HeaderWidget>
        </div>

        {/* Chat history - only visible when expanded, takes remaining space */}
        {isExpanded ? (
          <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:mt-4 tw:overflow-hidden">
            <div className="tw:px-2.5 tw:text-sm tw:font-medium tw:text-primary tw:shrink-0">
              Chats
            </div>
            <div className="tw:flex-1 tw:overflow-y-auto tw:mt-2">
              <HistoryList
                conversations={data?.conversations || []}
                isLoading={isLoading}
                error={error ? "Failed to load conversations" : null}
                onSelect={handleConversationClick}
                currentConversationId={currentConversationId}
                widgetId="concierge-sidebar-history"
                displayName="Sidebar History"
                showHeader={false}
                emptyMessage="No conversations yet"
                initialState={{
                  // Customize styling for sidebar - more compact
                  itemPaddingX: 8,
                  itemPaddingY: 4,
                  itemTitleLineClamp: 1,
                  timestampMarginTop: 2,
                  timestampFontSize: 11,
                  drawerBgColor: "transparent",
                }}
              />
            </div>
          </div>
        ) : (
          /* Spacer - only when collapsed to push avatar to bottom */
          <div className="tw:flex-1" />
        )}

        {/* User avatar at bottom - only shown when signed in */}
        {isSignedIn && (
          <div className="tw:relative">
            <button
              aria-label="Sign out"
              onMouseEnter={() => setAvatarHovered(true)}
              onMouseLeave={() => setAvatarHovered(false)}
              className="tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:mb-2 tw:cursor-pointer tw:w-full"
            >
              <div
                className="tw:flex tw:items-center tw:justify-center tw:size-5 tw:rounded-full tw:text-[8px] tw:font-semibold tw:leading-[12px] tw:text-white tw:shrink-0"
                style={{ backgroundColor: theme.palette.text.primary }}
              >
                {initials}
              </div>
              <span
                className="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150"
                style={{
                  opacity: isExpanded ? 1 : 0,
                  width: isExpanded ? "auto" : 0,
                  color: theme.palette.text.primary,
                }}
              >
                Sign out
              </span>
            </button>
            {/* Tooltip for avatar when collapsed */}
            {!isExpanded && avatarHovered && (
              <div
                className="tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:bg-primary tw:text-white tw:rounded-lg tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  lineHeight: "18px",
                }}
              >
                Sign out
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
};
